import { UI } from "@wwimmo/ui";
import * as Sentry from "@sentry/react";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { useCallback, useContext, useState } from "react";
import { RootStoreContext } from "src/stores/RootStore";
import { LOADING_TYPE } from "src/stores/krediflow/InvoiceEnums";
import styles from "./InvoiceWorkflowTab.module.css";
import { Link } from "react-router-dom";
import { Route, selectRoute } from "src/config/routes";
import { SCREEN_SIZE } from "src/stores/UIStore";

const InvoiceBatchProcessTabBase = () => {
    const { t } = useTranslation();
    const { invoiceStore, authStore, invoiceListStore, uiStore } = useContext(RootStoreContext);

    const [isMenuPointClicked, setIsMenuPointClicked] = useState<boolean>(false);

    const onClickPreviousBatchProcessInvoice = useCallback(() => {
        invoiceListStore.setPreviousBatchProcessIndex();
    }, [invoiceListStore]);

    const onClickNextBatchProcessInvoice = useCallback(() => {
        invoiceListStore.setNextBatchProcessIndex();
    }, [invoiceListStore]);

    const onClickTerminateBatchProcess = useCallback(() => {
        invoiceListStore.terminateBatchProcess();
    }, [invoiceListStore]);

    const onClickMenuPoints = useCallback(() => {
        setIsMenuPointClicked(!isMenuPointClicked);
    }, [isMenuPointClicked]);

    return invoiceListStore.isBatchProcessing ? (
        <div
            className={`${styles.InvoiceBatchProcessTabContainer} d-flex justify-content-center position-relative ${
                uiStore.currentScreenSize > SCREEN_SIZE.LAPTOP_L ? "flex-row" : "flex-column"
            }`}
        >
            {invoiceStore.loadingType === LOADING_TYPE.INITIALIZING ? (
                <div className={styles.LoadingContainer}>
                    <UI.RotatingSpinner noLogo size={25} className={styles.LoadingTicketSpinner} />
                    <div className="ml-2">{t("screens.kredi_flow.loading_batch_processes")}</div>
                </div>
            ) : (
                <>
                    <div className={`${styles.BatchProcessNavigationContainer} d-flex align-items-center`}>
                        <div className={`${styles.BatchProcessNavigationIconContainer} mr-2`}>
                            <Link
                                onClick={onClickPreviousBatchProcessInvoice}
                                to={selectRoute(Route.invoiceDetails, authStore.user?.role, {
                                    invoicenumber: invoiceListStore.previousBatchProcessInvoiceNumber.toString()
                                })}
                            >
                                <UI.Icon
                                    icon={UI.SVGIcon.ChevronLeft}
                                    size={25}
                                    className={styles.BatchProcessNavigationBackIcon}
                                />
                            </Link>
                        </div>
                        <div>
                            <span style={{ fontWeight: "400", fontSize: "30px" }}>
                                {invoiceListStore.currentBatchProcessIndex + 1}
                            </span>
                            <span className="mx-1">/</span>
                            <span style={{ fontSize: "18px" }}>
                                {invoiceListStore.selectedBatchProcessInvoiceNumbers.length}
                            </span>
                        </div>
                        <div className={`${styles.BatchProcessNavigationIconContainer} ml-2`}>
                            <Link
                                onClick={onClickNextBatchProcessInvoice}
                                to={selectRoute(Route.invoiceDetails, authStore.user?.role, {
                                    invoicenumber: invoiceListStore.nextBatchProcessInvoiceNumber.toString()
                                })}
                            >
                                <UI.Icon
                                    icon={UI.SVGIcon.ChevronRight}
                                    size={25}
                                    className={styles.BatchProcessNavigationNextIcon}
                                />
                            </Link>
                        </div>
                    </div>
                    {uiStore.currentScreenSize > SCREEN_SIZE.MOBILE_L ? (
                        <div
                            className={`${
                                uiStore.currentScreenSize > SCREEN_SIZE.LAPTOP_L
                                    ? "position-absolute"
                                    : "position-static"
                            }`}
                            style={{ right: "0px" }}
                        >
                            <Link
                                onClick={onClickTerminateBatchProcess}
                                to={selectRoute(Route.creditors, authStore.user?.role)}
                            >
                                <span className="error-text">{t("screens.kredi_flow.finish_batch_processing")}</span>
                            </Link>
                        </div>
                    ) : (
                        <div className={styles.BatchProcessingMobileMenuPoints} onClick={onClickMenuPoints}>
                            ...
                        </div>
                    )}
                    {isMenuPointClicked && (
                        <div className={styles.BatchProcessMenuPointsContainer}>
                            <Link
                                onClick={onClickTerminateBatchProcess}
                                to={selectRoute(Route.creditors, authStore.user?.role)}
                            >
                                <span>{t("screens.kredi_flow.finish_batch_processing")}</span>
                            </Link>
                        </div>
                    )}
                </>
            )}
        </div>
    ) : (
        <></>
    );
};

export const InvoiceBatchProcessTab = Sentry.withProfiler(observer(InvoiceBatchProcessTabBase));
